import { jwtDecode } from 'jwt-decode';
import { fetchWithAuth } from './authToken';
import API_BASE_URL from './urlHelper';
import jwtUtils from '../utilities/jwtUtils';

const ROLE_LABELS = {
  admin: 'ADMINISTRADOR',
  asesor: 'ASESOR',
  auditor: 'AUDITOR',
  cajero: 'CAJERO',
  cliente: 'CLIENTE',
  contabilidad: 'CONTABILIDAD',
  operaciones: 'OPERACIONES',
};

function normalizeText(value) {
  return String(value ?? '')
    .replace(/\s+/g, ' ')
    .trim();
}

function buildInitials(fullName) {
  const parts = normalizeText(fullName).split(' ').filter(Boolean);

  if (parts.length === 0) {
    return 'US';
  }

  return parts
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

function safeDecodeToken(token) {
  if (!token) {
    return null;
  }

  try {
    return jwtDecode(token);
  } catch (error) {
    return null;
  }
}

function flattenErrorDetails(details) {
  if (!details) {
    return [];
  }

  if (Array.isArray(details)) {
    return details.flatMap(flattenErrorDetails);
  }

  if (typeof details === 'object') {
    return Object.values(details).flatMap(flattenErrorDetails);
  }

  return [String(details)];
}

function isGenericErrorMessage(message) {
  const normalized = normalizeText(message).toLowerCase();

  return [
    'error de validacion.',
    'error de validación.',
    'errores de validacion',
    'errores de validación',
    'datos inválidos',
    'datos invalidos',
    'the given data was invalid.',
  ].includes(normalized);
}

function isTechnicalErrorMessage(message) {
  const normalized = normalizeText(message).toLowerCase();

  return [
    'sqlstate[',
    'unknown column',
    'base table or view not found',
    'stack trace',
    'syntax error or access violation',
    'uncaught',
    'exception',
    'select * from',
    'insert into',
    'update `',
  ].some((pattern) => normalized.includes(pattern));
}

function extractErrorMessage(payload, fallbackMessage) {
  const detailCandidates = [...flattenErrorDetails(payload?.details), ...flattenErrorDetails(payload?.errors)]
    .map((message) => normalizeText(message))
    .filter(Boolean)
    .filter((message) => !isTechnicalErrorMessage(message));

  const primaryMessage = normalizeText(payload?.message);
  const safePrimaryMessage = isTechnicalErrorMessage(primaryMessage) ? '' : primaryMessage;

  if (detailCandidates.length > 0 && (!safePrimaryMessage || isGenericErrorMessage(safePrimaryMessage))) {
    return detailCandidates[0];
  }

  const candidates = [safePrimaryMessage, ...detailCandidates].filter(Boolean);

  return candidates[0] || fallbackMessage;
}

async function readResponsePayload(response) {
  try {
    const raw = await response.text();
    const text = normalizeText(raw);

    if (!text) {
      return null;
    }

    try {
      return JSON.parse(raw);
    } catch (parseError) {
      if (text.startsWith('<!DOCTYPE') || text.startsWith('<html')) {
        return null;
      }

      return { message: text };
    }
  } catch (error) {
    return null;
  }
}

function getRoleLabel(role) {
  const normalizedRole = normalizeText(role).toLowerCase();
  return ROLE_LABELS[normalizedRole] || normalizeText(role).toUpperCase() || 'USUARIO';
}

function normalizeProfile(profile) {
  const fullName = normalizeText(profile?.fullName);

  return {
    id: profile?.id ?? null,
    kind: normalizeText(profile?.kind).toLowerCase() || 'usuario',
    role: normalizeText(profile?.role).toLowerCase() || null,
    roleLabel: getRoleLabel(profile?.role),
    username: normalizeText(profile?.username),
    email: normalizeText(profile?.email),
    fullName: fullName || 'Usuario',
    firstName: normalizeText(profile?.firstName),
    apellidoPaterno: normalizeText(profile?.apellidoPaterno),
    apellidoMaterno: normalizeText(profile?.apellidoMaterno),
    initials: buildInitials(fullName || profile?.username || 'Usuario'),
  };
}

export function getFallbackAccountProfile() {
  const refreshPayload = jwtUtils.getValidRefreshTokenPayload();
  const accessPayload = safeDecodeToken(jwtUtils.getAccessTokenFromCookie());
  const role = refreshPayload?.rol || accessPayload?.rol || null;
  const fullName = normalizeText(accessPayload?.nombre || accessPayload?.username || 'Usuario');

  return normalizeProfile({
    id: refreshPayload?.sub || accessPayload?.sub || null,
    kind: role === 'cliente' ? 'cliente' : 'usuario',
    role,
    username: accessPayload?.username || '',
    email: accessPayload?.email || '',
    fullName,
  });
}

export async function getAccountProfile() {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/account/profile`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });

  const payload = await readResponsePayload(response);

  if (!response.ok) {
    throw new Error(extractErrorMessage(payload, 'No se pudo cargar tu perfil.'));
  }

  return normalizeProfile(payload?.profile ?? payload?.data ?? payload);
}

export async function changeAccountPassword({ currentPassword, password, passwordConfirmation }) {
  const response = await fetchWithAuth(`${API_BASE_URL}/api/account/password`, {
    method: 'PATCH',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      current_password: currentPassword,
      password,
      password_confirmation: passwordConfirmation,
    }),
  });

  const payload = await readResponsePayload(response);

  if (!response.ok) {
    throw new Error(extractErrorMessage(payload, 'No se pudo actualizar la contraseña.'));
  }

  return {
    message: payload?.message || 'Contraseña actualizada correctamente.',
    reauthRequired: Boolean(payload?.reauth_required),
  };
}